<div class="sku-management-container">
  <p-toast></p-toast>

  <p-confirmDialog
    key="skuConfirm"
    [styleClass]="
      'max-w-md w-[28rem] rounded-xl shadow-2xl border-0 overflow-hidden'
    "
    [maskStyleClass]="'backdrop-blur-sm bg-black/40'"
    appendTo="body"
  >
    <ng-template pTemplate="message" let-message>
      <div class="px-3 pl-0 py-2">
        <p class="text-gray-800 dark:text-gray-200 leading-6">
          {{ message?.message | translate }}
        </p>
      </div>
    </ng-template>
  </p-confirmDialog>

  <!-- Deactivate reason dialog (Activate keeps the plain p-confirmDialog above) -->
  <p-dialog
    [visible]="showDeactivateReasonDialog()"
    (visibleChange)="showDeactivateReasonDialog.set($event)"
    [modal]="true"
    [closable]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '90vw', maxWidth: '500px' }"
    [header]="'sku_management.confirmations.deactivate_title' | translate"
    (onHide)="onCancelDeactivateDialog()"
  >
    <label
      for="deactivate-reason"
      class="block text-sm font-medium text-gray-700 mb-2"
    >
      {{ "sku_management.dialog.deactivate_reason_label" | translate }}
    </label>
    <textarea
      id="deactivate-reason"
      pTextarea
      [rows]="4"
      [autoResize]="true"
      class="w-full"
      [placeholder]="
        'sku_management.dialog.deactivate_reason_placeholder' | translate
      "
      [ngModel]="deactivateReason()"
      (ngModelChange)="deactivateReason.set($event)"
    ></textarea>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <app-button
          [label]="'sku_management.confirmations.cancel' | translate"
          type="secondary"
          [outlined]="true"
          (emitOnClick)="onCancelDeactivateDialog()"
        ></app-button>
        <app-button
          [label]="'sku_management.confirmations.deactivate' | translate"
          type="danger"
          (emitOnClick)="onConfirmDeactivate()"
        ></app-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Header with Tabs -->
  <div class="sku-management-header">
    <h2 class="text-2xl font-semibold text-gray-800 mb-3">
      {{ "sku_management.title" | translate }}
    </h2>

    <!-- Tab Navigation -->
    <div>
      <button
        class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
        [class]="
          activeTab() === 'ordinary'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        "
        (click)="onTabChange('ordinary')"
      >
        {{ "sku_management.tabs.ordinary_sku_list" | translate }}
      </button>
      <button
        class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
        [class]="
          activeTab() === 'package'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        "
        (click)="onTabChange('package')"
      >
        {{ "sku_management.tabs.package_sku_list" | translate }}
      </button>
      <button
        class="px-6 py-3 text-sm font-medium border-b-2 transition-colors"
        [class]="
          activeTab() === 'draft'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        "
        (click)="onTabChange('draft')"
      >
        {{ "sku_management.tabs.draft_sku_list" | translate }}
      </button>
    </div>
  </div>

  <!-- Search Form with Status Filter -->
  <div class="sku-management-actions">
    <app-search-form
      [searchFormat]="searchFields()"
      (formSearch)="onSearch($event)"
      (formReset)="onReset()"
    >
      <div slot="actions" class="flex gap-2">
        <app-button
          type="success"
          [outlined]="true"
          icon="view_column"
          (emitOnClick)="onColumnConfigClick($event)"
        ></app-button>
        @if (canApprove()) {
          <app-button
            [label]="'sku_management.actions.activate' | translate"
            type="success"
            [outlined]="true"
            [disabled]="!canActivateSelectedItems()"
            (emitOnClick)="onBulkActivate()"
          ></app-button>
          <app-button
            [label]="'sku_management.actions.deactivate' | translate"
            type="danger"
            [outlined]="true"
            [disabled]="!canDeactivateSelectedItems()"
            (emitOnClick)="onBulkDeactivate()"
          ></app-button>
        }
        <app-status-filter
          [primaryButton]="primaryButton()"
          [secondaryButton]="secondaryButton()"
          (primaryButtonClick)="onprimaryButtonClick()"
          (secondaryonClick)="onSecondaryButtonClick()"
        ></app-status-filter>
      </div>
    </app-search-form>
  </div>

  <!-- Data Table -->
  <div class="sku-management-table flex-table-section">
    <advanced-data-table
      #advancedTable
      [dataSource]="skuItems()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [totalRecords]="totalItems()"
      [rows]="itemsPerPage()"
      [first]="currentPage() * itemsPerPage()"
      scrollHeight="flex"
      [hasCheckColumn]="true"
      [selection]="selectedItems()"
      (pageChange)="onPageChange($event)"
      (selectionChange)="onSelectionChange($event)"
      [showInternalColumnConfigButton]="false"
      class="w-full"
    >
      <!-- Custom Template for Category, Currency, and Date -->
      <ng-template
        #customTemplate
        let-value
        let-rowData="rowData"
        let-column="column"
      >
        @if (column && column.template === "dateTemplate") {
          <span class="text-xs text-gray-600">
            {{ formatDateTime(value) }}
          </span>
        } @else if (column && column.template === "currencyTemplate") {
          <span class="text-xs text-gray-600 text-right">
            {{ formatCurrency(value) }}
          </span>
        } @else if (column && column.template === "categoryTemplate") {
          <span
            class="text-xs text-gray-700 truncate cursor-help"
            [pTooltip]="value"
            tooltipPosition="top"
            tooltipStyleClass="custom-tooltip"
            [showDelay]="300"
            [hideDelay]="0"
          >
            {{ value | lastCategory }}
          </span>
        } @else if (column && column.template === "vendorTemplate") {
          @if (rowData.vendorList && rowData.vendorList.length > 0) {
            <div class="vendor-list-container flex flex-col gap-1">
              @if (rowData.vendorList.length <= 2) {
                <!-- show all if <= 2 vendors -->
                @for (vendorName of rowData.vendorList; track vendorName) {
                  <span
                    class="text-xs text-gray-700 truncate"
                    [pTooltip]="vendorName"
                    tooltipPosition="top"
                    tooltipStyleClass="custom-tooltip text-xs max-w-md"
                    [showDelay]="300"
                    [hideDelay]="0"
                  >
                    {{ vendorName }}
                  </span>
                }
              } @else {
                <!-- show first 2 vendors + "+N more" -->
                <div class="flex flex-col gap-1">
                  <span
                    class="text-xs text-gray-700 truncate"
                    [pTooltip]="rowData.vendorList[0]"
                    tooltipPosition="top"
                    tooltipStyleClass="custom-tooltip text-xs max-w-md"
                    [showDelay]="300"
                    [hideDelay]="0"
                  >
                    {{ rowData.vendorList[0] }}
                  </span>
                  <div class="flex items-center gap-1">
                    <span
                      class="text-xs text-gray-700 truncate"
                      [pTooltip]="rowData.vendorList[1]"
                      tooltipPosition="top"
                      tooltipStyleClass="custom-tooltip text-xs max-w-md"
                      [showDelay]="300"
                      [hideDelay]="0"
                    >
                      {{ rowData.vendorList[1] }}
                    </span>
                    <span
                      class="text-xs text-blue-600 cursor-pointer"
                      [pTooltip]="rowData.vendorList.join('\n')"
                      tooltipPosition="top"
                      tooltipStyleClass="custom-tooltip whitespace-pre-line text-xs max-w-md"
                      [showDelay]="300"
                      [hideDelay]="0"
                    >
                      ...
                    </span>
                  </div>
                </div>
              }
            </div>
          }
        } @else if (column && column.template === "statusTemplate") {
          <div class="flex items-center justify-center">
            <p-tag
              [value]="getStatusDisplayText(rowData.status) | translate"
              [severity]="getStatusSeverity(rowData.status)"
              styleClass="status-tag"
              [ngStyle]="getStatusStyle(rowData.status)"
              pTooltip="{{ getStatusDisplayText(rowData.status) | translate }}"
              tooltipPosition="top"
              tooltipStyleClass="custom-tooltip"
              [showDelay]="300"
              [hideDelay]="0"
            />
          </div>
        }
      </ng-template>

      <!-- Custom Actions Template -->
      <ng-template #actionsTemplate let-rowData>
        <div class="flex gap-2 justify-center">
          @if (canRead()) {
            <span
              (click)="onView(rowData)"
              class="action-link action-view cursor-pointer"
            >
              {{ "sku_management.actions.view" | translate }}
            </span>
          }
          @if (canUpdate() && canEditSkuInline(rowData)) {
            <span
              (click)="onEdit(rowData)"
              class="action-link action-edit cursor-pointer"
            >
              {{ "sku_management.actions.edit" | translate }}
            </span>
          }
          @if (canUpdate() && canSubmitSkuInline(rowData)) {
            <span
              (click)="onSubmitItem(rowData)"
              class="action-link action-submit cursor-pointer"
            >
              {{ "sku_management.actions.submit" | translate }}
            </span>
          }
          @if (canApprove() && canActivateSku(rowData)) {
            <span
              (click)="onActivateItem(rowData)"
              class="action-link action-enable cursor-pointer"
            >
              {{ "sku_management.actions.activate" | translate }}
            </span>
          }
          @if (canApprove() && canDeactivateSku(rowData)) {
            <span
              (click)="onDeactivateItem(rowData)"
              class="action-link action-disable cursor-pointer"
            >
              {{ "sku_management.actions.deactivate" | translate }}
            </span>
          }
          @if (hasOthersActions(rowData)) {
            <span
              class="action-link action-others cursor-pointer"
              aria-haspopup="menu"
              (click)="toggleActionsMenu($event, rowData)"
            >
              {{ "common.actions.others" | translate }}
            </span>
          }
        </div>
      </ng-template>
    </advanced-data-table>
  </div>
</div>

<!-- Single Menu for Approve / Reject / Delete -->
<p-menu
  #actionMenu
  [popup]="true"
  [model]="currentMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  [autoZIndex]="true"
></p-menu>
